<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import DefaultLayout from '@/layouts/Layout.vue';
import {
  getTrimmedCloudAppUrl,
  isCloudAppPortalUnit,
  cloudAppPortalTabLabel,
  unitManagementPath,
} from '@/tools/cloudAppUnit';

const route = useRoute();
const router = useRouter();
const store = useStore();

const dataID = computed(() => route.params.dataID);
const portalUrl = ref('');
const loadError = ref('');
const iframeKey = ref(0);

const friendlyName = computed(() => store.state.device?.friendlyName || dataID.value);

const iframeTitle = computed(() => {
  const d = store.state.device;
  if (d && isCloudAppPortalUnit(d)) return `${friendlyName.value} - ${cloudAppPortalTabLabel(d)}`;
  return `${friendlyName.value} cloud app`;
});

function reloadIframe() {
  iframeKey.value += 1;
}

onMounted(async () => {
  loadError.value = '';
  try {
    let unit = store.state.device;
    if (!unit || unit.dataID !== dataID.value) {
      const snap = await getDoc(doc(db, 'data', dataID.value));
      if (!snap.exists()) {
        loadError.value = 'Unit not found.';
        return;
      }
      unit = { ...snap.data(), dataID: dataID.value };
      store.dispatch('setDevice', unit);
    }

    if (!isCloudAppPortalUnit(unit)) {
      await router.replace(unitManagementPath(dataID.value));
      return;
    }

    const url = getTrimmedCloudAppUrl(unit);
    portalUrl.value = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
  } catch (e) {
    console.error('Cloud app portal load failed:', e);
    loadError.value = 'Could not load this unit.';
  }
});
</script>

<template>
  <DefaultLayout :dataID="dataID">
    <div class="flex flex-col flex-1 min-h-0 min-w-0 bg-mesh-surface-card dark:bg-mesh-surface-dark rounded-lg overflow-hidden">
      <div
        class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/50 shrink-0"
      >
        <div class="min-w-0 flex-1">
          <p class="text-xs text-gray-500 dark:text-gray-400 truncate">
            Embedded view - some sites block embedding; use open in new tab if the frame stays blank.
          </p>
          <div class="mt-2 flex flex-wrap items-center gap-2">
            <router-link
              to="/v2/analyze"
              class="rounded-md border border-mesh-line px-2 py-1 text-[11px] text-mesh-text-primary hover:bg-mesh-neutral"
            >
              Analyze Shell
            </router-link>
            <router-link
              to="/v2/tools"
              class="rounded-md border border-mesh-line px-2 py-1 text-[11px] text-mesh-text-primary hover:bg-mesh-neutral"
            >
              Tools Shell
            </router-link>
            <span class="text-[11px] text-mesh-text-muted">
              Legacy deep-link:
              <span class="font-mono">/dashboard/{{ dataID }}/portal</span>
            </span>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2 shrink-0">
          <button
            type="button"
            class="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            @click="reloadIframe"
          >
            Reload
          </button>
          <a
            v-if="portalUrl"
            :href="portalUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-mesh-brand-blue text-white hover:opacity-90"
          >
            Open in new tab
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>

      <div v-if="loadError" class="flex-1 flex items-center justify-center p-6 text-sm text-red-600 dark:text-red-400">
        {{ loadError }}
      </div>
      <iframe
        v-else-if="portalUrl"
        :key="iframeKey"
        :src="portalUrl"
        class="w-full flex-1 min-h-[480px] border-0 bg-white dark:bg-gray-950"
        :title="iframeTitle"
        referrerpolicy="no-referrer-when-downgrade"
      />
      <div v-else class="flex-1 flex items-center justify-center p-6 text-sm text-gray-500">
        Loading…
      </div>
    </div>
  </DefaultLayout>
</template>
